import { useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useCoachDashboard } from '../hooks/coach/useCoachDashboard';
import RegisterPaymentDialog from '../components/dialogs/RegisterPaymentDialog';
import { UpcomingWorkoutsWidget } from '../components/coach/dashboard/UpcomingWorkoutsWidget';
import LiveAtletasWidget from '../components/coach/dashboard/LiveAtletasWidget';
import { PaymentDatesWidget } from '../components/coach/dashboard/PaymentDatesWidget';
import { RunningOutWidget } from '../components/coach/dashboard/RunningOutWidget';
import { ImportantDatesWidget } from '../components/coach/dashboard/ImportantDatesWidget';
import { LastTrainedWidget } from '../components/coach/dashboard/LastTrainedWidget';
import { RevenueChartWidget } from '../components/coach/dashboard/RevenueChartWidget';
import { BusinessAlertsWidget } from '../components/coach/dashboard/BusinessAlertsWidget';
import { TrainingConsistencyWidget } from '../components/coach/dashboard/TrainingConsistencyWidget';
import { OnboardingChecklist } from '../components/coach/dashboard/OnboardingChecklist';
import { TrialBanner } from '../components/coach/dashboard/TrialBanner';
import { useOnboardingChecklist } from '../hooks/coach/useOnboardingChecklist';
import { CoachProfileEditDialog } from '../components/coach/CoachProfileEditDialog';
import { useCoachProfileEditDialog } from '../hooks/coach/useCoachProfileEditDialog';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';
import { PlanDetailsDialog } from '../components/dialogs/PlanDetailsDialog';

function StatWidget({
  icon,
  iconBg,
  iconColor,
  value,
  label
}: {
  icon: string;
  iconBg: string;
  iconColor: string;
  value: string | number;
  label: string;
}): JSX.Element {
  return (
    <div
      className="flex align-items-center gap-3"
      style={{
        padding: '0.65rem 0.85rem',
        background: 'var(--ios-surface-subtle)',
        borderRadius: '14px',
        flex: '1 1 auto',
        minWidth: '0'
      }}
    >
      <div
        className="flex align-items-center justify-content-center"
        style={{
          width: '2.5rem',
          height: '2.5rem',
          background: iconBg,
          borderRadius: '12px',
          flexShrink: 0
        }}
      >
        <i className={icon} style={{ color: iconColor, fontSize: '1.05rem' }} />
      </div>
      <div>
        <div style={{ fontSize: '1.35rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--ios-text-secondary)', fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}

export default function CoachHomePage(): JSX.Element {
  // #6 ítem 4: tocar el nombre del entrenamiento en los widgets abre el preview
  // acá mismo, sin sacar al coach del dashboard.
  const [previewInstanceId, setPreviewInstanceId] = useState<number | null>(null);
  const {
    loading,
    coachName,
    formattedDate,
    upcomingSessions,
    paymentStatus,
    cycleStatus,
    lastTrainedDetails,
    trainingFrequency,
    importantDates,
    revenueData,
    businessAlerts,
    totalClients,
    totalPaid,
    unpaidCount,
    viewClientProfile,
    goToManageStudents,
    openPaymentDialog,
    closePaymentDialog,
    paymentDialogContext,
    refreshDashboard,
    intl
  } = useCoachDashboard();

  const onboarding = useOnboardingChecklist();
  const { coach } = useUser();
  const profileDialog = useCoachProfileEditDialog(coach);
  const { showToast } = useToast();

  return (
    <div style={{ padding: '0.75rem', minHeight: '100vh' }}>
      {/* Dashboard header */}
      <div
        style={{
          background: 'var(--ios-card-bg)',
          borderRadius: '16px',
          padding: '1rem',
          marginBottom: '0.75rem',
          border: '1px solid var(--ios-card-border)',
          boxShadow: 'var(--ios-card-shadow)'
        }}
      >
        <div className="flex flex-column lg:flex-row justify-content-between align-items-start lg:align-items-center gap-3">
          <div className="flex-1">
            <h1
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                margin: '0 0 0.25rem'
              }}
            >
              {intl.formatMessage({ id: 'coach.home.welcome' }, { name: coachName })}
            </h1>
            <p style={{ color: 'var(--ios-text-secondary)', fontSize: '0.95rem', margin: 0 }}>{formattedDate}</p>
          </div>

          <div className="flex flex-wrap gap-2 align-items-center">
            <StatWidget
              icon="pi pi-users"
              iconBg="rgba(163, 230, 53, 0.18)"
              iconColor="#4d7c0f"
              value={totalClients}
              label={intl.formatMessage({ id: 'coach.home.activeStudents' })}
            />
            <StatWidget
              icon="pi pi-check-circle"
              iconBg="rgba(34, 197, 94, 0.1)"
              iconColor="#22c55e"
              value={totalPaid}
              label={intl.formatMessage({ id: 'coach.home.paidUp' })}
            />
            <StatWidget
              icon="pi pi-exclamation-triangle"
              iconBg="rgba(249, 115, 22, 0.1)"
              iconColor="#f97316"
              value={unpaidCount}
              label={intl.formatMessage({ id: 'coach.home.unpaidClients' })}
            />
            <Button
              label={intl.formatMessage({ id: 'coach.home.manageStudents' })}
              icon="pi pi-user-edit"
              onClick={goToManageStudents}
              style={{
                background: '#a3e635',
                border: 'none',
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: '0.85rem',
                padding: '0.55rem 1rem'
              }}
            />
          </div>
        </div>
      </div>

      {onboarding.status?.trial && (
        <TrialBanner
          trial={onboarding.status.trial}
          onUpgradeClick={() => {
            // TODO Fase 4: redirigir al checkout de Mercado Pago.
            showToast(
              'info',
              intl.formatMessage({
                id: 'coach.trialBanner.comingSoon',
                defaultMessage: 'Suscripción Mercado Pago'
              }),
              intl.formatMessage({
                id: 'coach.trialBanner.comingSoonBody',
                defaultMessage: 'Estamos terminando la integración. Te avisamos en breve.'
              })
            );
          }}
        />
      )}
      {onboarding.showChecklist && onboarding.status && (
        <OnboardingChecklist
          status={onboarding.status}
          onDismiss={onboarding.dismiss}
          onTaskClick={(taskId) => {
            if (taskId === 'profile') {
              profileDialog.open();
              return true; // ya manejado, no navegar
            }
            // Cualquier otra tarea → dejar caer al navigate por default.
            return false;
          }}
        />
      )}

      <CoachProfileEditDialog
        state={profileDialog}
        onSaved={() => {
          // El backend ya guardó. Refrescamos el status del checklist para
          // que la tarea "Completar perfil" pase a hecha; el `coach` del
          // UserContext se rehidrata al volver a entrar al perfil.
          void onboarding.refetch();
          showToast(
            'success',
            intl.formatMessage({ id: 'common.success', defaultMessage: 'Listo' }),
            intl.formatMessage({
              id: 'coach.profileDialog.saved',
              defaultMessage: 'Tu perfil se actualizó'
            })
          );
        }}
        onError={(message) =>
          showToast('error', intl.formatMessage({ id: 'common.error', defaultMessage: 'Error' }), message)
        }
      />

      {/* Widget grid */}
      <div className="grid">
        {/* Row 0 — live coach feed (always shown, even with 0 atletas) */}
        <div className="col-12 p-2">
          <LiveAtletasWidget />
        </div>

        {/* Row 1 */}
        <div className="col-12 md:col-6 lg:col-4 p-2">
          <UpcomingWorkoutsWidget
            data={upcomingSessions}
            loading={loading}
            onClientClick={viewClientProfile}
            onWorkoutClick={setPreviewInstanceId}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4 p-2">
          <PaymentDatesWidget data={paymentStatus} loading={loading} onClientClick={openPaymentDialog} />
        </div>
        <div className="col-12 md:col-6 lg:col-4 p-2">
          <RunningOutWidget
            data={cycleStatus}
            loading={loading}
            onClientClick={viewClientProfile}
            onWorkoutClick={setPreviewInstanceId}
          />
        </div>

        {/* Row 2 */}
        <div className="col-12 md:col-6 lg:col-4 p-2">
          <LastTrainedWidget data={lastTrainedDetails} loading={loading} onClientClick={viewClientProfile} />
        </div>
        <div className="col-12 md:col-6 lg:col-4 p-2">
          <TrainingConsistencyWidget data={trainingFrequency} loading={loading} onClientClick={viewClientProfile} />
        </div>
        <div className="col-12 md:col-6 lg:col-4 p-2">
          <RevenueChartWidget data={revenueData} loading={loading} />
        </div>

        {/* Row 3 */}
        <div className="col-12 md:col-6 lg:col-4 p-2">
          <ImportantDatesWidget data={importantDates} loading={loading} />
        </div>
        <div className="col-12 md:col-6 lg:col-8 p-2">
          <BusinessAlertsWidget data={businessAlerts} loading={loading} onClientClick={viewClientProfile} />
        </div>
      </div>

      <Dialog
        draggable={false}
        resizable={false}
        dismissableMask
        header={intl.formatMessage({ id: 'students.dialog.registerPayment' })}
        className="responsive-dialog"
        visible={!!paymentDialogContext}
        style={{ width: '50vw' }}
        onHide={closePaymentDialog}
      >
        {paymentDialogContext && (
          <RegisterPaymentDialog
            studentId={paymentDialogContext.studentId}
            onClose={closePaymentDialog}
            oldSubscription={paymentDialogContext.oldSubscription}
            oldCoachPlan={paymentDialogContext.oldCoachPlan}
          />
        )}
      </Dialog>

      {/* #6 ítem 4: preview del entrenamiento desde los widgets. */}
      <PlanDetailsDialog
        visible={previewInstanceId !== null}
        onHide={() => setPreviewInstanceId(null)}
        planId={previewInstanceId}
        clientId=""
        // Si desde el preview se edita o elimina el entrenamiento, los widgets
        // tienen que reflejarlo sin recargar la página.
        setRefreshKey={refreshDashboard}
        setLoading={() => undefined}
      />
    </div>
  );
}
